import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put } from '@/lib/api'

import type { TimeStatus, WorkflowStage } from '@company/shared'


// ==================== Types ====================

export type { TimeStatus, WorkflowStage }

/**
 * @deprecated Use TimeStatus from @company/shared instead
 */
export type StageTimeStatus = TimeStatus

export interface StageEstimateInput {
  stage: WorkflowStage
  estimatedHours: number
  deadline?: string | null
}

export interface TimeConfigInput {
  finalDeadline?: string | null
  alertThresholdPercent?: number
  stageEstimates?: StageEstimateInput[]
}

export interface StageEstimateResult {
  id: string
  issueId: string
  stage: WorkflowStage
  estimatedHours: number
  deadline: string | null
  stageStartedAt: string | null
  stageCompletedAt: string | null
  actualHours: number | null
  variance: number | null
  timeStatus: StageTimeStatus
}

export interface TimeConfigResult {
  id: string
  issueId: string
  finalDeadline: string | null
  alertThresholdPercent: number
  stageEstimates: StageEstimateResult[]
  totalEstimatedHours: number
  createdAt: string
  updatedAt: string | null
}

// ==================== Query Keys ====================

export const timeEstimationKeys = {
  all: ['time-estimation'] as const,
  config: (issueId: string) => [...timeEstimationKeys.all, 'config', issueId] as const,
}

// ==================== Stage Display Names ====================

export const STAGE_ESTIMATE_DISPLAY_NAMES: Record<WorkflowStage, string> = {
  created: '已创建',
  product_discussion: '产品研讨',
  ui_design: 'UI设计',
  ui_confirmation: 'UI确认',
  frontend_development: '前端开发',
  backend_development: '后端开发',
  parallel_development: '并行开发',
  code_review: '代码审查',
  ops_deployment: '运维部署',
  testing: '测试',
  product_acceptance: '产品验收',
  done: '已完成',
}

export function getStageEstimateDisplayName(stage: string): string {
  return STAGE_ESTIMATE_DISPLAY_NAMES[stage as WorkflowStage] ?? stage
}

// ==================== Hooks ====================

/**
 * 获取任务时间配置
 * Requirements: 2.1, 2.5
 */
export function useTimeConfig(issueId: string) {
  return useQuery({
    queryKey: timeEstimationKeys.config(issueId),
    queryFn: () => get<TimeConfigResult | null>(`/pm/issues/${issueId}/time-config`),
    enabled: !!issueId,
  })
}

/**
 * 更新任务时间配置
 * Requirements: 2.1, 2.5, 3.4
 */
export function useUpdateTimeConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: TimeConfigInput }) =>
      put<TimeConfigResult>(`/pm/issues/${issueId}/time-config`, data),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: timeEstimationKeys.config(issueId) })
    },
  })
}

// ==================== Utility Functions ====================

/**
 * 计算截止日期倒计时（毫秒）
 * 负数表示已超时
 */
export function calculateDeadlineCountdown(deadline: string | null): number | null {
  if (!deadline) {return null}
  const deadlineTime = new Date(deadline).getTime()
  const now = Date.now()
  return deadlineTime - now
}

/**
 * 格式化倒计时显示
 */
export function formatCountdown(countdownMs: number | null): string {
  if (countdownMs === null) {return '-'}

  const isOverdue = countdownMs < 0
  const absMs = Math.abs(countdownMs)

  const hours = Math.floor(absMs / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24

  if (days > 0) {
    return isOverdue
      ? `已超时 ${days}天${remainingHours}小时`
      : `剩余 ${days}天${remainingHours}小时`
  }

  if (hours > 0) {
    return isOverdue ? `已超时 ${hours}小时` : `剩余 ${hours}小时`
  }

  const minutes = Math.floor(absMs / (1000 * 60))
  return isOverdue ? `已超时 ${minutes}分钟` : `剩余 ${minutes}分钟`
}

/**
 * 获取时间状态的颜色类名
 */
export function getTimeStatusColor(status: StageTimeStatus): string {
  switch (status) {
    case 'on_track':
      return 'text-green-600 dark:text-green-400'
    case 'approaching_deadline':
      return 'text-yellow-600 dark:text-yellow-400'
    case 'overdue':
      return 'text-red-600 dark:text-red-400'
    default:
      return 'text-muted-foreground'
  }
}

/**
 * 获取时间状态的背景颜色类名
 */
export function getTimeStatusBgColor(status: StageTimeStatus): string {
  switch (status) {
    case 'on_track':
      return 'bg-green-100 dark:bg-green-900/30'
    case 'approaching_deadline':
      return 'bg-yellow-100 dark:bg-yellow-900/30'
    case 'overdue':
      return 'bg-red-100 dark:bg-red-900/30'
    default:
      return 'bg-muted'
  }
}

/**
 * 获取时间状态显示名称
 */
export function getStageTimeStatusDisplayName(status: StageTimeStatus): string {
  switch (status) {
    case 'on_track':
      return '正常'
    case 'approaching_deadline':
      return '即将超时'
    case 'overdue':
      return '已超时'
    default:
      return status
  }
}
